'use client';
import { useEffect, useRef, useState } from 'react';
import { useT, useSite } from '../lib/i18n';
import type { MenuNode, QuickLink } from '../lib/site';
import { SmartLink } from './SmartLink';
import { Icon } from './Icon';
import { openPlayer, playerHref } from './player/player-shared';

/**
 * 모바일 메뉴(햄버거 드로어) — 가이드 `mobilemenu.html` **E안**(2026-09-02 사용자 확정).
 *
 *  위    로그인 시 [최근 본 강의] + [정기구독 한 줄] + [나의 현황 넷] / 비로그인 시 [로그인·회원가입]
 *  가운데 [바로가기] 넷 — 설정 > 테마 > 헤더에서 고른 것(서버가 못 쓰는 항목을 걸러 넷을 준다)
 *  아래   1뎁스 > 2뎁스 태그칩 — **항상 펼친 상태, 펼침 아이콘 없음**(사용자 지시)
 *
 * 가이드의 `mm-e-sticky`(하단 고정 로그인 버튼)와 `mm-e-toggle`(로그인 전/후 미리보기)은
 * 가이드 안에서 두 상태를 보여 주는 **시연 장치**라 그리지 않는다.
 *
 * 회원 요약(최근 수강·상태칸)은 **서랍을 열 때** 한 번 받는다 — 헤더가 매 페이지 그 값을
 * 물고 다닐 까닭이 없고, 서랍을 안 여는 방문에는 요청도 없다.
 */
const ICON_RECENT = 'circle-play';
const ICON_PLAY = 'play';
const ICON_SUB = 'package';
const ICON_CHEVRON = 'chevron-right';

interface MobileSummary {
  recent: {
    enrollmentId: number; name: string; lessonNo: number | null; lessonName: string; progress: number;
    /** 재생 주소 재료 — 구독 시청이면 `bmid`·`upid` 가 차 있다(일반 수강은 둘 다 null) */
    bmid: number | null; upid: number | null; productId: number; curriculumId: number;
  } | null;
  /** 정기구독 — 최근 본 강의 아래 한 줄. 없으면 그 줄을 그리지 않는다 */
  subscription: { planName: string; endDate: string | null; dday: number | null; warn: boolean } | null;
  /** 나의 현황 칸 — PC 카드와 같은 목록·같은 순서를 서버가 준다. `warn` 이면 값을 붉게 */
  status: Array<{ key: string; value: string | number; dot?: boolean; warn?: boolean }>;
}

/**
 * 최근 본 차시의 재생 주소 — 갈래가 둘이다(2026-09-17 사용자 지시 "구독도 되어야지").
 * 일반 수강은 수강권(`cid`), 구독은 계약과 진입 상품(`bmid`·`upid`)으로 주소가 갈린다.
 */
const recentHref = (r: NonNullable<MobileSummary['recent']>) =>
  playerHref(r.bmid && r.upid ? { bmid: r.bmid, upid: r.upid } : { cid: r.enrollmentId }, r.productId, r.curriculumId);

export function MobileDrawer({ open, menus, quickLinks, loggedIn, onNavigate }: {
  open: boolean;
  /** 헤더 상단 노출 메뉴 트리(1뎁스 → 2뎁스) */
  menus: MenuNode[];
  quickLinks: QuickLink[];
  loggedIn: boolean;
  /** 링크를 눌렀을 때 — 서랍을 닫는다(헤더가 넘겨 준다) */
  onNavigate: (e: React.MouseEvent) => void;
}) {
  const t = useT();
  const { lang } = useSite();
  const pick = (name: Record<string, string> | null) => name?.[lang] ?? name?.ko ?? '';
  const [summary, setSummary] = useState<MobileSummary | null>(null);
  /*
   * 못 받아 왔나 — **모르는 것을 아는 척하지 않는다**(2026-09-04 사용자 제보).
   * 401 이 와도 계속 "불러오는 중" 을 그리고 있었다. 못 받았으면 그 자리를 아예 비운다
   * (401 이면 `Alert` 가 셸을 다시 받아 로그인 버튼이 제자리로 돌아온다).
   */
  const [failed, setFailed] = useState(false);
  const box = useRef<HTMLElement>(null);

  /*
   * 열 때는 **언제나 맨 위**(2026-09-03 사용자 지시).
   * 서랍은 닫아도 DOM 에 남고 클래스만 떨어진다 — 그래서 아래까지 굴려 놓고 닫으면
   * 다음에 열 때 그 자리 그대로 보였다. 열리는 순간 스크롤을 되돌린다.
   */
  useEffect(() => { if (open) box.current?.scrollTo({ top: 0 }); }, [open]);

  useEffect(() => {
    if (!open || !loggedIn || summary || failed) return;
    let alive = true;
    void fetch('/api/members/me/enrollments/mobile-summary')
      .then((r) => (r.ok ? (r.json() as Promise<MobileSummary>) : null))
      .then((d) => { if (!alive) return; if (d) setSummary(d); else setFailed(true); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [open, loggedIn, summary, failed]);

  /**
   * 칸 값 — **기준을 값에 함께 적는다**(2026-09-08 사용자 확정). 라벨은 PC 카드와 같은 이름이라
   * 짧고, 값 한 줄이 전부이므로 `오늘 3강` 처럼 무엇을 센 것인지가 값에 들어가야 뜻이 통한다.
   */
  const statusValue = (c: { key: string; value: string | number }) => {
    if (typeof c.value === 'string') return c.value; // 정기구독의 `D-12`
    switch (c.key) {
      case 'study': return t('mc.st.studyVal', { n: c.value });
      case 'total': return t('mc.st.totalVal', { n: c.value });
      case 'device': return t('mc.st.deviceVal', { n: c.value });
      case 'qna': return t('mc.st.qnaVal', { n: c.value });
      default: return String(c.value);
    }
  };

  return (
    <nav ref={box} className={`drawer${open ? ' on' : ''}`} onClick={onNavigate}>
      {/* ── 위: 로그인 시 최근 수강·상태칸 / 비로그인 시 [로그인 / 회원가입] 한 버튼 ── */}
      {!loggedIn && (
        /*
         * 가이드의 `mm-e-sticky-btn` **한 개**(2026-09-02 사용자 지적 — 둘로 나눴다가 고침).
         * 자리만 다르다 — 가이드는 화면 아래 고정(`mm-e-sticky`)인데 사용자 지시로 최근 수강내역
         * 자리(맨 위)에 둔다. 버튼 모양·크기는 가이드 클래스 그대로다.
         */
        <div className="mb20">
          <SmartLink href="/login" className="mm-e-sticky-btn">{t('mm.loginJoin')}</SmartLink>
        </div>
      )}
      {loggedIn && !failed && (
      <div className="mm-quick">
          <>
            <p className="mm-quick-label"><Icon name={ICON_RECENT} /> {t('mm.recent')}</p>
            {summary?.recent ? (
              <div className="mm-quick-item">
                <div className="mm-quick-info">
                  {/* 강좌명은 작은 회색 줄, 차시명이 제목(2026-09-08 사용자 확정 — PC 카드와 같은 구조) */}
                  <p className="mm-quick-sub">{summary.recent.name}</p>
                  <p className="mm-quick-title">
                    {/* 번호만 붙인다 — 단위 글자('강')는 자리를 먹어 뺐다(2026-09-08 사용자 지시) */}
                    {summary.recent.lessonNo != null && <>{summary.recent.lessonNo}. </>}
                    {summary.recent.lessonName}
                  </p>
                  <div className="mm-quick-progress">
                    <div className="lecture-bar">
                      {/* 진도 바는 늘 메인 컬러(2026-09-07 사용자 확정) — 가이드의 30% 미만 빨강은 쓰지 않는다 */}
                      <div className="rate-fill rate-fill-high"
                        style={{ width: `${Math.min(100, summary.recent.progress)}%` }} />
                    </div>
                    <span className="mm-quick-pct">{summary.recent.progress}%</span>
                  </div>
                </div>
                {/*
                  **마지막으로 본 그 차시**를 연다(2026-09-17 — 플레이어가 생겨 강의실 대신 재생으로 바꿨다).
                  일반 수강은 수강권, 구독은 계약과 진입 상품으로 주소가 갈린다. 휴대폰에서는 같은 탭이 앱 안내로 넘어간다.
                */}
                <a href={recentHref(summary.recent)} className="mm-quick-go" aria-label={t('mm.continue')}
                  onClick={(e) => { e.preventDefault(); openPlayer(recentHref(summary.recent!)); }}>
                  <Icon name={ICON_PLAY} />
                </a>
              </div>
            ) : (
              <div className="mm-quick-item">
                <div className="mm-quick-info">
                  <p className="mm-quick-title txt-deactivate">{summary ? t('mm.noRecent') : t('common.loading')}</p>
                </div>
              </div>
            )}
          </>
      </div>
      )}

      {/*
        정기구독 — **나의 현황 칸이 아니라 최근 본 강의 아래 한 줄**(2026-09-08 사용자 확정).
        구독을 안 쓰는 사이트나 결제하지 않은 회원에게는 이 줄 자체가 없다. 그래서 아래 현황 칸은
        언제나 넷 그대로다. 모양은 최근 본 강의와 같은 조각(`mm-quick`)을 쓴다.
      */}
      {loggedIn && summary?.subscription && (
        <div className="mm-quick mm-quick-sub-block">
          <p className="mm-quick-label"><Icon name={ICON_SUB} /> {t('mc.card.sub')}</p>
          <div className="mm-quick-item">
            <div className="mm-quick-info">
              <p className="mm-quick-title">{summary.subscription.planName}</p>
              <p className={`mm-quick-sub${summary.subscription.warn ? ' dday-warn' : ''}`}>
                {t('mc.sub.nextPay')} {summary.subscription.dday != null ? t('mc.dday', { n: summary.subscription.dday }) : '-'}
              </p>
            </div>
            <SmartLink href="/lectures?billing=1" className="mm-quick-go" aria-label={t('mc.sub.courses')}>
              <Icon name={ICON_CHEVRON} />
            </SmartLink>
          </div>
        </div>
      )}

      {/* 나의 현황 — 서버가 순서를 정해 준다(PC 카드와 같은 목록). 비로그인이면 안 그린다 */}
      {loggedIn && summary && summary.status.length > 0 && (
        <div className="mm-status">
          {summary.status.map((c) => (
            <div className="mm-status-item" key={c.key}>
              <p className="mm-status-label">
                {t(`mc.st.${c.key}`)}
                {c.dot && <span className="mm-status-dot"></span>}
              </p>
              {/* 남은 날이 사흘 이하면 붉게 — 날짜 자체가 신호다(가이드 `dday-warn`) */}
              <p className={`mm-status-value${c.warn ? ' dday-warn' : ''}`}>{statusValue(c)}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── 바로가기 — 설정 > 테마 > 헤더. 서버가 걸러 준 넷이라 여기서 다시 판단하지 않는다 ── */}
      {quickLinks.length > 0 && (
        <div className="mm-quick3-group">
          <div className="mm-group-head">
            <span className="mm-group-label">{t('mm.quick')}</span>
            <span className="mm-group-line"></span>
          </div>
          <div className="mm-quick3">
            {quickLinks.map((q) => (
              <SmartLink key={q.key} href={q.href} className="mm-quick3-item">
                <span className="mm-quick3-icon"><Icon name={q.icon} /></span>
                <span className="mm-quick3-label">{t(`quick.${q.key}`)}</span>
              </SmartLink>
            ))}
          </div>
        </div>
      )}

      {/* ── 1뎁스 > 2뎁스 — 항상 펼침, 펼침 아이콘 없음 ── */}
      {menus.map((m) => (
        m.children.length > 0 ? (
          <div key={m.id} className="mm-a-item mm-a-item_e open">
            <div className="mm-a-head mm-a-head_e">
              <SmartLink href={m.url} target={m.openNewWindow ? '_blank' : undefined}>{pick(m.name)}</SmartLink>
            </div>
            <div className="mm-a-panel">
              <div className="mm-d-chips">
                {m.children.map((c) => (
                  <SmartLink key={c.id} href={c.url} className="mm-d-chip" target={c.openNewWindow ? '_blank' : undefined}>
                    {pick(c.name)}
                  </SmartLink>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <SmartLink key={m.id} href={m.url} className="mm-a-plain mm-a-plain_e" target={m.openNewWindow ? '_blank' : undefined}>
            {pick(m.name)}
          </SmartLink>
        )
      ))}

      {/*
        설정(말·화면 모드) — 휴대폰에서는 여기가 유일한 길이다(2026-09-04 사용자 요청).
        마이페이지 좌측 메뉴의 다크 토글은 768px 아래에서 통째로 숨는다(가이드 CSS).
        로그인과 무관한 값이라 로그인 여부를 가리지 않는다.
      */}
      <SmartLink href="/settings" className="mm-a-plain mm-a-plain_e">{t('set.go')}</SmartLink>

      {/* 로그인했으면 맨 아래 로그아웃 — 헤더 우측 메뉴와 같은 길 */}
      {loggedIn && (
        <SmartLink href="/logout" className="mm-e-sticky-btn">{t('shell.logout')}</SmartLink>
      )}
    </nav>
  );
}
